import React from 'react';

import { SENDING_TYPE_OPTIONS } from '@vlabs/api-bindings/src/luna-client/constants';
import { InfoCircleIcon } from '@vlabs/icons';
import validate from '@vlabs/shared/validators';
import { Control, SettingsItemWrapper, Tooltip } from '@vlabs/uikit';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { defaultValues } from '@vlabs/pages/video-analytics/defaultFormValues';

import { DroiForm } from './DroiForm';
import { EventPolicySubForm } from './EventPolicySubForm';
import { SaveToArchiveSubForm } from './SaveToArchiveSubForm';
import st from './SharedSubFormStyles.module.sass';
import { TargetsOverviewSubForm } from './TargetsOverviewSubForm';
import {
  BagsThresholdSettingsModal,
  BagsThresholdSettingsTooltipPreview,
} from '../../modal/BagsThresholdSettingsModal';
import { FrameAnalysisFrequencyModal } from '../../modal/FrameAnalysisFrequencyModal';

export function BagsSubForm({ fieldName }) {
  const { t } = useTranslation();
  const createFullName = (name) => (fieldName ? `${fieldName}.${name}` : name);

  const { register, control, formState: { errors } } = useFormContext();

  const frameRateAnalysisType = useWatch({ name: createFullName('parameters.parameters.rate.unit') })?.value;
  const commonStylesProps = {
    className: st.SettingItem,
    titleClassName: st.SettingItem__Title,
    contentClassName: st.SettingItem__Content,
  };

  return (
    <React.Fragment>
      <div className={st.SettingsGroup}>
        <div className={st.SettingsGroup__Header}>
          <span>{t('videoAnalytics:form.bags.threshold_settings.title')}</span>
          <Tooltip
            className={st.Tooltip}
            modalContent={<BagsThresholdSettingsModal />}
            tooltipContent={<BagsThresholdSettingsTooltipPreview />}
            tooltipProps={{ effect: 'solid' }}
          >
            <InfoCircleIcon className={st.Tooltip_icon} />
          </Tooltip>
        </div>

        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.bags.bag_confidence_trh.tooltip')}
          title={t('videoAnalytics:form.bags.bag_confidence_trh.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.bag_confidence_trh'), {
              min: validate.gte(0),
              max: validate.lte(1),
              validate: validate.number(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          title={t('videoAnalytics:form.bags.min_lenght_lost.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.min_lenght_lost'), {
              min: validate.gte(1),
              validate: validate.integer(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>
      </div>

      <div className={st.SettingsGroup}>
        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.bags.area_trh.tooltip')}
          title={t('videoAnalytics:form.bags.area_trh.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.area_trh'), {
              min: validate.gte(0),
              validate: validate.integer(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          title={t('videoAnalytics:form.rate.unit.label')}
          {...commonStylesProps}
        >
          <Control.Select
            control={control}
            isClearable
            name={createFullName('parameters.parameters.rate.unit')}
            options={SENDING_TYPE_OPTIONS.raw}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.rate.period.tooltip')}
          modalContent={<FrameAnalysisFrequencyModal />}
          title={t('videoAnalytics:form.rate.period.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.rate.period'), {
              min: frameRateAnalysisType === 'frame'
                ? validate.gte(1)
                : validate.gte(0.001),
              validate: frameRateAnalysisType === 'frame'
                ? validate.integer()
                : validate.number(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>

        <EventPolicySubForm fieldName={fieldName} />

        <SettingsItemWrapper
          title={t('videoAnalytics:form.ROI.label')}
          {...commonStylesProps}
        >
          <Controller
            control={control}
            name={createFullName('parameters.parameters.roi')}
            render={({ field: { value, onChange } }) => {
              return (
                <Control.CoordinatesInput
                  errors={errors}
                  name={createFullName('parameters.parameters.roi')}
                  onChange={onChange}
                  value={value ?? defaultValues.roi}
                  variant="modern"
                />
              );
            }}
            rules={{ validate: validate.region }}
          />
        </SettingsItemWrapper>

        <DroiForm name={createFullName('parameters.parameters.droi')} />

        <TargetsOverviewSubForm
          analytic="bags"
          fieldName={fieldName}
        />

        <SaveToArchiveSubForm fieldName={fieldName} />
      </div>

      <div className={st.SettingsGroup}>
        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.bags.iou_static_trh.tooltip')}
          title={t('videoAnalytics:form.bags.iou_static_trh.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.iou_static_trh'), {
              min: validate.gte(0),
              max: validate.lte(1),
              validate: validate.number(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.bags.human_bbox_expand_coeff.tooltip')}
          title={t('videoAnalytics:form.bags.human_bbox_expand_coeff.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.human_bbox_expand_coeff'), {
              min: validate.gte(0),
              max: validate.lte(1),
              validate: validate.number(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          data-tooltip-content={t('videoAnalytics:form.bags.human_intersection_num_frames_trh.tooltip')}
          title={t('videoAnalytics:form.bags.human_intersection_num_frames_trh.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.human_intersection_num_frames_trh'), {
              min: validate.gte(1),
              validate: validate.integer(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>

        <SettingsItemWrapper
          title={t('videoAnalytics:form.bags.human_intersection_new_range_trh.label')}
          {...commonStylesProps}
        >
          <Control.Input
            {...register(createFullName('parameters.parameters.human_intersection_new_range_trh'), {
              min: validate.gte(5),
              validate: validate.integer(),
            })}
            errors={errors}
          />
        </SettingsItemWrapper>
      </div>
    </React.Fragment>
  );
}
